import { integrationsApi } from "@/lib/api";
import {
  isGoogleWorkspaceSheetsReconnectPass,
  parseGoogleIntegrationStatus,
  type GoogleIntegrationStatus,
} from "@/lib/googleIntegrationStatus";
import { readLastWorkspace } from "@/lib/lastWorkspaceStorage";
import { GOOGLE_SHEETS_CONNECTED_COOKIE } from "@/lib/googleSheetsConnectedCookie";

const STORAGE_KEY = "google_integration_status_v1";
/** In-memory — short TTL for hot path within a session. */
const MEMORY_TTL_MS = 15_000;
/** localStorage — longer TTL for instant reads on reload / re-entry. */
const STORAGE_TTL_MS = 5 * 60 * 1000;

type StoredGoogleIntegrationStatus = {
  fetchedAt: number;
  status: GoogleIntegrationStatus;
};

let inflight: Promise<GoogleIntegrationStatus> | null = null;
let inflightFresh: Promise<GoogleIntegrationStatus> | null = null;
let cached: StoredGoogleIntegrationStatus | null = null;

function readStoredStatus(): StoredGoogleIntegrationStatus | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw?.trim()) return null;
    const parsed = JSON.parse(raw) as Partial<StoredGoogleIntegrationStatus>;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof parsed.fetchedAt !== "number" ||
      !parsed.status ||
      typeof parsed.status !== "object"
    ) {
      return null;
    }
    return {
      fetchedAt: parsed.fetchedAt,
      status: parsed.status as GoogleIntegrationStatus,
    };
  } catch {
    return null;
  }
}

function clearGoogleSheetsConnectedCookie(): void {
  if (typeof document === "undefined") return;
  try {
    document.cookie = `${GOOGLE_SHEETS_CONNECTED_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    // ignore
  }
}

function syncGoogleSheetsConnectedCookie(status: GoogleIntegrationStatus): void {
  if (typeof document === "undefined") return;
  const workspaceId = readLastWorkspace()?.trim() || null;
  const connected = isGoogleWorkspaceSheetsReconnectPass(status, workspaceId);
  try {
    if (connected) {
      const maxAgeSec = Math.floor(STORAGE_TTL_MS / 1000);
      document.cookie = `${GOOGLE_SHEETS_CONNECTED_COOKIE}=1; path=/; max-age=${maxAgeSec}; SameSite=Lax`;
    } else {
      clearGoogleSheetsConnectedCookie();
    }
  } catch {
    // ignore
  }
}

function writeStoredStatus(status: GoogleIntegrationStatus): void {
  if (typeof window === "undefined") return;
  try {
    const entry: StoredGoogleIntegrationStatus = {
      fetchedAt: Date.now(),
      status,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    syncGoogleSheetsConnectedCookie(status);
  } catch {
    // ignore quota / private mode
  }
}

function hydrateMemoryFromStorage(now = Date.now()): boolean {
  if (cached && now - cached.fetchedAt < MEMORY_TTL_MS) return true;
  const stored = readStoredStatus();
  if (!stored || now - stored.fetchedAt >= STORAGE_TTL_MS) return false;
  cached = stored;
  return true;
}

async function fetchGoogleIntegrationStatusFromNetwork(
  force = false,
): Promise<GoogleIntegrationStatus> {
  if (force) {
    if (inflightFresh) return inflightFresh;
  } else if (inflight) {
    return inflight;
  }

  const request = (async () => {
    const res = (await integrationsApi.getGoogleStatus()) as Record<
      string,
      unknown
    >;
    const status = parseGoogleIntegrationStatus(res);
    const entry: StoredGoogleIntegrationStatus = {
      fetchedAt: Date.now(),
      status,
    };
    cached = entry;
    writeStoredStatus(status);
    return status;
  })();

  if (force) {
    inflightFresh = request;
  } else {
    inflight = request;
  }
  try {
    return await request;
  } finally {
    if (force) {
      if (inflightFresh === request) {
        inflightFresh = null;
      }
    } else if (inflight === request) {
      inflight = null;
    }
  }
}

export function invalidateGoogleIntegrationStatusCache(): void {
  cached = null;
  inflight = null;
  inflightFresh = null;
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  clearGoogleSheetsConnectedCookie();
}

/** Synchronous read — instant when a recent value exists in localStorage. */
export function readGoogleIntegrationStatusCacheSync(): GoogleIntegrationStatus | null {
  const now = Date.now();
  if (cached && now - cached.fetchedAt < STORAGE_TTL_MS) {
    return cached.status;
  }
  const stored = readStoredStatus();
  if (!stored || now - stored.fetchedAt >= STORAGE_TTL_MS) return null;
  cached = stored;
  return stored.status;
}

export function readGoogleIntegrationStatusCacheAgeMs(): number | null {
  const now = Date.now();
  if (cached && now - cached.fetchedAt < STORAGE_TTL_MS) {
    return now - cached.fetchedAt;
  }
  const stored = readStoredStatus();
  if (!stored || now - stored.fetchedAt >= STORAGE_TTL_MS) return null;
  return now - stored.fetchedAt;
}

/** Fire-and-forget warm-up — hydrates from localStorage then refreshes via API. */
export function prefetchGoogleIntegrationStatus(): void {
  hydrateMemoryFromStorage();
  void fetchGoogleIntegrationStatus().catch(() => {
    // ignore warm-up failures
  });
}

/**
 * Fetch Google integration status.
 * - Returns memory / localStorage immediately when fresh (stale-while-revalidate).
 * - Still calls API in the background when localStorage is older than memory TTL.
 * - `force: true` always awaits a fresh API response.
 */
export async function fetchGoogleIntegrationStatus(options?: {
  force?: boolean;
}): Promise<GoogleIntegrationStatus> {
  const now = Date.now();

  if (options?.force) {
    return fetchGoogleIntegrationStatusFromNetwork(true);
  }

  if (cached && now - cached.fetchedAt < MEMORY_TTL_MS) {
    return cached.status;
  }

  const stored = readStoredStatus();
  if (stored && now - stored.fetchedAt < STORAGE_TTL_MS) {
    cached = stored;
    return stored.status;
  }

  return fetchGoogleIntegrationStatusFromNetwork();
}

if (typeof window !== "undefined") {
  window.addEventListener("auth:logout", () => {
    invalidateGoogleIntegrationStatusCache();
  });
}
